import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, BarChart3, Package, Building2, Users, Sliders } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { quotationAPI, customerAPI, branchAPI } from '../../services/api';
import DateRangeFilter, { filterByDateRange } from './DateRangeFilter';
import SubCategorySalesReport from './SubCategorySalesReport';
import BranchProductSalesReport from './BranchProductSalesReport';
import PartyProductSalesReport from './PartyProductSalesReport';
import CustomReportBuilder from './CustomReportBuilder';
import { usePermissions } from '../../hooks/usePermissions';

const COLORS = ['#24549c', '#3a6fc4', '#FF9800', '#6A1B9A', '#1a3d73', '#E65100', '#4CAF50'];

const SalesReport = () => {
  const { hasPermission } = usePermissions();
  const canViewSubCat = hasPermission('reports.sub_category_sales');
  const canViewOverview = hasPermission('reports.sales');
  const initialSection = canViewOverview ? 'overview' : (canViewSubCat ? 'subcat' : 'overview');
  const [section, setSection] = useState(initialSection); // 'overview' | 'subcat' | 'branch' | 'party'
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('customer');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [q, c, b] = await Promise.all([
          quotationAPI.getAll(),
          customerAPI.getAll().catch(() => []),
          branchAPI.getAll().catch(() => []),
        ]);
        setQuotes(q); setCustomers(c); setBranches(b);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const filtered = useMemo(() => {
    const dateFiltered = filterByDateRange(quotes, dateRange);
    return dateFiltered.filter(q => statusFilter === 'All' || q.status === statusFilter);
  }, [quotes, statusFilter, dateRange]);

  const totalValue = filtered.reduce((s, q) => s + (q.total || 0), 0);
  const totalQuotes = filtered.length;
  const accepted = filtered.filter(q => q.status === 'Accepted').length;
  const sent = filtered.filter(q => q.status === 'Sent').length;
  const conversionRate = totalQuotes > 0 ? ((accepted / totalQuotes) * 100).toFixed(0) : 0;
  const avgQuoteValue = totalQuotes > 0 ? Math.round(totalValue / totalQuotes) : 0;

  // Customer-wise
  const customerData = useMemo(() => {
    const map = {};
    filtered.forEach(q => {
      const name = q.customer_name || customers.find(c => c.id === q.customer)?.name || 'Unknown';
      if (!map[name]) map[name] = { name, value: 0, count: 0, accepted: 0 };
      map[name].value += (q.total || 0);
      map[name].count++;
      if (q.status === 'Accepted') map[name].accepted++;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filtered, customers]);

  // Status-wise
  const statusData = useMemo(() => {
    const map = {};
    filtered.forEach(q => {
      if (!map[q.status]) map[q.status] = { name: q.status, value: 0, count: 0 };
      map[q.status].value += (q.total || 0);
      map[q.status].count++;
    });
    return Object.values(map);
  }, [filtered]);

  // Product-wise (from quote items)
  const productData = useMemo(() => {
    const map = {};
    filtered.forEach(q => {
      (q.items || []).forEach(item => {
        const name = item.name || 'Unknown';
        if (!map[name]) map[name] = { name, value: 0, qty: 0, quotes: 0 };
        map[name].value += (item.amount || item.qty * item.rate || 0);
        map[name].qty += (item.qty || 0);
        map[name].quotes++;
      });
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filtered]);

  const currentData = view === 'customer' ? customerData : view === 'status' ? statusData : productData;

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(r => Object.values(r).join(','));
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} /></div>;

  const SectionSwitcher = () => (
    <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-5">
      {canViewOverview && (
        <button data-testid="sales-section-overview" onClick={() => setSection('overview')}
          className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${section === 'overview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <BarChart3 className="w-3.5 h-3.5" /> Quotation Analytics
        </button>
      )}
      {canViewSubCat && (
        <button data-testid="sales-section-subcat" onClick={() => setSection('subcat')}
          className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${section === 'subcat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Package className="w-3.5 h-3.5" /> Salesperson Performance
        </button>
      )}
      <button data-testid="sales-section-branch" onClick={() => setSection('branch')}
        className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${section === 'branch' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
        <Building2 className="w-3.5 h-3.5" /> Branch Distribution
      </button>
      <button data-testid="sales-section-party" onClick={() => setSection('party')}
        className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${section === 'party' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
        <Users className="w-3.5 h-3.5" /> Customer Purchases
      </button>
      <button data-testid="sales-section-custom" onClick={() => setSection('custom')}
        className={`flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${section === 'custom' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
        <Sliders className="w-3.5 h-3.5" /> Custom Reports
      </button>
    </div>
  );

  if (section === 'subcat' && canViewSubCat) {
    return (
      <div>
        <SectionSwitcher />
        <SubCategorySalesReport />
      </div>
    );
  }

  if (section === 'branch') {
    return (
      <div>
        <SectionSwitcher />
        <BranchProductSalesReport />
      </div>
    );
  }

  if (section === 'party') {
    return (
      <div>
        <SectionSwitcher />
        <PartyProductSalesReport />
      </div>
    );
  }

  if (section === 'custom') {
    return (
      <div>
        <SectionSwitcher />
        <CustomReportBuilder />
      </div>
    );
  }

  if (!canViewOverview && canViewSubCat) {
    // Fallback: only subcat permission — auto-render it
    return (
      <div>
        <SectionSwitcher />
        <SubCategorySalesReport />
      </div>
    );
  }

  return (
    <div>
      <SectionSwitcher />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Sales Analytics</h2>
          <p className="text-xs text-gray-500">Customer-wise, product-wise & status breakdown of quotations</p>
        </div>
        <button onClick={() => exportCSV(currentData, `sales-${view}`)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Download className="w-3.5 h-3.5" /> Export CSV</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        {[
          { l: 'Total Quotation Value', v: `₹${(totalValue / 100000).toFixed(1)}L` },
          { l: 'Total Quotes', v: totalQuotes },
          { l: 'Accepted', v: accepted },
          { l: 'Sent / Pending', v: sent },
          { l: 'Conversion Rate', v: `${conversionRate}%` },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
            <p className="text-[10px] text-gray-500">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <DateRangeFilter onChange={setDateRange} />
        <div className="flex gap-1.5 ml-auto">
          {[{ id: 'customer', label: 'Customer-wise' }, { id: 'status', label: 'Status-wise' }, { id: 'product', label: 'Product-wise' }].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${view === t.id ? 'text-white' : 'text-gray-600 bg-gray-100'}`} style={view === t.id ? { backgroundColor: '#24549c' } : {}}>{t.label}</button>
          ))}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">
          <option value="All">All Status</option>
          {['Draft', 'Sent', 'Accepted', 'Rejected'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">{view === 'customer' ? 'Customer' : view === 'status' ? 'Status' : 'Product'} — Value</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={currentData.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
              <Bar dataKey="value" fill="#24549c" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={currentData.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value" nameKey="name">
                {currentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {currentData.filter(d => d.value > 0).slice(0, 7).map((d, i) => (
              <span key={i} className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-100 bg-gray-50">
            {['Name', ...(view === 'customer' ? ['Quotes', 'Accepted'] : view === 'product' ? ['Qty', 'Appearances'] : ['Count']), 'Value (₹)'].map(h => (
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {currentData.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-gray-400">No data found</td></tr>
            ) : (
              currentData.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold text-gray-900">{r.name}</td>
                  {view === 'customer' && <><td className="px-4 py-3 text-gray-500">{r.count}</td><td className="px-4 py-3 text-green-600">{r.accepted}</td></>}
                  {view === 'product' && <><td className="px-4 py-3 text-gray-500">{r.qty}</td><td className="px-4 py-3 text-gray-500">{r.quotes}</td></>}
                  {view === 'status' && <td className="px-4 py-3 text-gray-500">{r.count}</td>}
                  <td className="px-4 py-3 font-semibold">₹{r.value.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesReport;
